import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import React from 'react';
import { TimeUnits } from '../../Config/MedicationConfig';
import { numberInputFilter } from '../../Shared/InputFilters';
import { divider, errorText, warningGlowText } from '../../Styles/Colours';
import { MaskedTextField } from '../MaskedTextField';
import { isSingleSelectSingleValue, StyledSelect } from '../StyledSelect';
import styles from './DurationPicker.module.scss';

export const renderItemToString = (item: { value: TimeUnits }): string => {
    switch (item.value) {
        case TimeUnits.Days:
            return 'Days';
        case TimeUnits.Months:
            return 'Months';
        case TimeUnits.Hours:
            return 'Hours';
        case TimeUnits.Minutes:
            return 'Minutes';
        case TimeUnits.Seconds:
            return 'Seconds';
    }
};

export const renderItemToMenuKey = (item: { value: TimeUnits }): string => item.value.toString();

export type DurationType = { value: TimeUnits };
export const selectableDurationTypes: DurationType[] = [{ value: TimeUnits.Days }, { value: TimeUnits.Months }];

export type DurationPickerTimeLength = {
    number: string;
    timeUnits: TimeUnits;
};

type DurationPickerProps = {
    required?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    timeLength: DurationPickerTimeLength;
    error?: React.ReactNode;
    warning?: React.ReactNode;
    info?: React.ReactNode;
    hideLabel?: boolean;
    hideWarningEmptyHelperText?: boolean;
    inputHeight?: number;
    onUpdateTimeLength(timeLength: DurationPickerTimeLength): void;
    testId: string;
};

const useStyles = makeStyles({
    container: (props: DurationPickerProps) => ({
        display: 'grid',
        gridTemplateColumns: 155,
        gridTemplateRows: `${props.hideLabel ? 0 : '16px'} ${props.inputHeight ?? 58}px`,
        gridRowGap: 2,
        gridColumnStart: 1
    }),
    infoLine: (props: DurationPickerProps) => ({
        color: props.error ? errorText : props.warning ? warningGlowText : divider,
        marginTop: 4,
        marginBottom: 2,
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.66,
        minHeight: 19
    })
});

export const DurationPicker: React.FC<DurationPickerProps> = props => {
    const dynamicStyles = useStyles(props);
    const infoLine = props.error || props.warning || props.info;

    return (
        <div className={dynamicStyles.container} data-test-id={props.testId}>
            {!props.hideLabel && (
                <div className={styles.durationLabel}>
                    <InputLabel
                        shrink
                        className={classNames({
                            [styles.readOnlyLabel]: props.readOnly
                        })}
                    >
                        Course duration
                        {props.required && <span className={styles.requiredLabel}> *</span>}
                    </InputLabel>
                </div>
            )}
            <div className={styles.durationSelector}>
                <div className={styles.durationValue}>
                    <MaskedTextField
                        value={props.timeLength.number}
                        hiddenLabel
                        fullWidth
                        filterFunction={numberInputFilter}
                        onClear={() =>
                            props.onUpdateTimeLength({
                                number: '',
                                timeUnits: props.timeLength.timeUnits
                            })
                        }
                        onChange={value => {
                            const enteredNumber = Number(value);
                            if (value && !isNaN(enteredNumber) && enteredNumber <= 999 && enteredNumber >= 1) {
                                props.onUpdateTimeLength({
                                    number: value,
                                    timeUnits: props.timeLength.timeUnits
                                });
                            } else {
                                props.onUpdateTimeLength({
                                    number: '',
                                    timeUnits: props.timeLength.timeUnits
                                });
                            }
                        }}
                        testId={'duration-picker-number'}
                        readOnly={props.readOnly}
                        disabled={props.disabled}
                        errorText={props.error}
                        hideErrorText
                        hideWarningEmptyHelperText
                    />
                </div>
                <div className={styles.durationType}>
                    <StyledSelect<DurationType>
                        isClearable={false}
                        maxResults={3}
                        value={{
                            value: props.timeLength.timeUnits
                        }}
                        itemToString={renderItemToString}
                        itemToMenuKey={renderItemToMenuKey}
                        onSelection={newDurationType => {
                            if (newDurationType && isSingleSelectSingleValue(newDurationType)) {
                                props.onUpdateTimeLength({
                                    number: props.timeLength.number,
                                    timeUnits: newDurationType.value
                                });
                            }
                        }}
                        suggestions={selectableDurationTypes}
                        testId={'duration-picker-type'}
                        readOnly={props.readOnly}
                        disabled={props.disabled}
                        hideWarningEmptyHelperText
                    />
                </div>
            </div>
            {(infoLine || !props.hideWarningEmptyHelperText) && (
                <div className={dynamicStyles.infoLine}>{infoLine}</div>
            )}
        </div>
    );
};
